import classNames from 'classnames';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { Close, Code, DoubleChevronDown, DoubleChevronUp, Remove } from '@air/next-icons';
import { useToasts } from '@air/provider-toast';

import { AnalyticsTab } from '~/components/DevModal/components/AnalyticsTab';
import { APIRequestsTab } from '~/components/DevModal/components/APIRequestsTab';
import { ElementInspectorOverlay } from '~/components/DevModal/components/ElementInspectorOverlay';
import { FeatureFlagsTab } from '~/components/DevModal/components/FeatureFlagsTab';
import { ModalsTab } from '~/components/DevModal/components/ModalsTab';
import { OverviewTab } from '~/components/DevModal/components/OverviewTab';
import { ToolsTab } from '~/components/DevModal/components/ToolsTab';
import { useAnalyticsInterceptor } from '~/components/DevModal/hooks/useAnalyticsInterceptor';
import { useDragResize } from '~/components/DevModal/hooks/useDragResize';
import { useElementInspector } from '~/components/DevModal/hooks/useElementInspector';
import { useFetchInterceptor } from '~/components/DevModal/hooks/useFetchInterceptor';
import {
  type AnalyticsEvent,
  type APIRequest,
  type DevModalProps,
  type ElementInfo,
  type FlagValue,
} from '~/components/DevModal/types';

const COLLAPSED_HEIGHT = 70;

export const DevModal = memo(function DevModal({ isOpen, onClose }: DevModalProps) {
  // UI state
  const [activeTab, setActiveTab] = useState<'overview' | 'flags' | 'api' | 'tools' | 'modals' | 'analytics'>(
    'overview',
  );
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [wasCollapsed, setWasCollapsed] = useState(false);

  // Modal positioning and sizing
  const [position, setPosition] = useState({ x: 50, y: 50 });
  const [size, setSize] = useState({ width: 630, height: 600 });
  const [leftPanelWidth, setLeftPanelWidth] = useState(430);
  const [analyticsLeftPanelWidth, setAnalyticsLeftPanelWidth] = useState(430);

  // Feature state
  const [flagOverrides, setFlagOverrides] = useState<Record<string, FlagValue>>({});
  const [expandedFlags, setExpandedFlags] = useState<Record<string, boolean>>({});
  const [jsonInputs, setJsonInputs] = useState<Record<string, string>>({});
  const [isInspecting, setIsInspecting] = useState(false);
  const [elementInfo, setElementInfo] = useState<ElementInfo | null>(null);
  const [apiRequests, setApiRequests] = useState<APIRequest[]>([]);
  const [analyticsEvents, setAnalyticsEvents] = useState<AnalyticsEvent[]>([]);

  const { showToast } = useToasts();

  // Memoize tabs configuration to prevent re-creation on every render
  const tabs = useMemo(
    () =>
      [
        { id: 'overview' as const, label: 'Overview' },
        { id: 'flags' as const, label: 'Feature Flags' },
        { id: 'api' as const, label: 'API Requests' },
        { id: 'tools' as const, label: 'Tools' },
        { id: 'modals' as const, label: 'Modals' },
        { id: 'analytics' as const, label: 'Analytics' },
      ] as const,
    [],
  );

  useEffect(() => {
    if (isOpen) {
      // Reset to default size when opening
      setSize({ width: 630, height: 600 });
    }
  }, [isOpen]);

  // Utility functions
  const copy = useCallback(
    (text?: string) => {
      if (text) {
        navigator.clipboard.writeText(text);
        showToast('Copied to clipboard!');
      }
    },
    [showToast],
  );

  const serializeHeaders = useCallback((headers?: HeadersInit): Record<string, string> | undefined => {
    if (!headers) return undefined;
    try {
      if (headers instanceof Headers) return Object.fromEntries(Array.from(headers.entries()));
      if (Array.isArray(headers)) return Object.fromEntries(headers.map(([k, v]) => [k, String(v)]));
      if (headers && typeof headers === 'object' && !Array.isArray(headers)) {
        return Object.fromEntries(Object.entries(headers as Record<string, string>).map(([k, v]) => [k, String(v)]));
      }
      return undefined;
    } catch (error) {
      console.warn('[DevModal] Error serializing headers:', error);
      return undefined;
    }
  }, []);

  const shouldTrackUrl = useCallback(
    (url: string) =>
      (url.includes('/api/') ||
        url.includes('.air.inc') ||
        url.includes('localhost') ||
        url.includes('vercel.app') ||
        /\/graphql$/.test(url)) &&
      !/monitoring\.|telemetry\.|analytics\.|sentry\.|amplitude\.|mixpanel\.|hotjar\.|googletagmanager\.|google-analytics\.|\/ingest/.test(
        url,
      ),
    [],
  );

  // Event handlers
  const handleRequestCaptured = useCallback((request: APIRequest) => {
    setApiRequests((prev) => [request, ...prev].slice(0, 50));
  }, []);

  const handleAnalyticsEventCaptured = useCallback((event: AnalyticsEvent) => {
    setAnalyticsEvents((prev) => [event, ...prev].slice(0, 100));
  }, []);

  // ---------- init size/position + load overrides ----------
  useEffect(() => {
    if (typeof window === 'undefined') return;
    // Use our default 800px width instead of calculating from window width
    const maxWidth = 630;
    const maxHeight = Math.min(window.innerHeight * 0.8, 600);
    setSize({ width: maxWidth, height: maxHeight });
    // Position modal on the right side of the screen
    setPosition({ x: window.innerWidth - maxWidth - 20, y: window.innerHeight - maxHeight - 20 });

    const saved = localStorage.getItem('dev-flag-overrides');
    if (saved) {
      try {
        setFlagOverrides(JSON.parse(saved));
      } catch (error) {
        console.warn('[DevModal] Error parsing saved flag overrides:', error);
      }
    }
  }, []);

  // Listen for flag override changes from other instances
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'dev-flag-overrides') {
        try {
          const newOverrides = e.newValue ? JSON.parse(e.newValue) : {};
          setFlagOverrides(newOverrides);
        } catch (error) {
          console.warn('[DevModal] Error parsing storage change data:', error);
        }
      }
    };

    const handleCustomEvent = () => {
      try {
        const saved = localStorage.getItem('dev-flag-overrides');
        if (saved) {
          setFlagOverrides(JSON.parse(saved));
        }
      } catch (error) {
        console.warn('[DevModal] Error parsing custom event data:', error);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('dev-flag-override-changed', handleCustomEvent);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('dev-flag-override-changed', handleCustomEvent);
    };
  }, []);

  // expose for debugging
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.__devFlagOverrides = flagOverrides;
    }
  }, [flagOverrides]);

  // ---------- flag overrides ----------
  const handleToggleFlag = useCallback((flagName: string, newValue: FlagValue | undefined) => {
    setFlagOverrides((prev) => {
      let next: Record<string, FlagValue>;
      if (newValue === undefined) {
        // Reset to original value
        const { [flagName]: _removed, ...rest } = prev;
        next = rest;
      } else {
        // Otherwise, set the override
        next = { ...prev, [flagName]: newValue };
      }
      localStorage.setItem('dev-flag-overrides', JSON.stringify(next));
      window.dispatchEvent(new CustomEvent('dev-flag-override-changed'));
      return next;
    });
  }, []);

  const handleResetFlags = useCallback(() => {
    setFlagOverrides({});
    localStorage.removeItem('dev-flag-overrides');
    window.dispatchEvent(new CustomEvent('dev-flag-override-changed'));
  }, []);

  // Custom hooks for complex logic
  useFetchInterceptor({
    isOpen,
    shouldTrackUrl,
    onRequestCaptured: handleRequestCaptured,
    serializeHeaders,
  });

  const { isDragging, isResizing, onHeaderMouseDown, onResizeMouseDown, toggleCollapse } = useDragResize({
    isCollapsed,
    wasCollapsed,
    setIsCollapsed,
    setWasCollapsed,
    position,
    setPosition,
    size,
    setSize,
  });

  useAnalyticsInterceptor({
    isOpen,
    onEventCaptured: handleAnalyticsEventCaptured,
  });

  useElementInspector({
    isInspecting,
    setElementInfo,
  });

  // Keyboard shortcuts
  useEffect(() => {
    if (!isOpen) return;

    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'c') {
        e.preventDefault();
        toggleCollapse();
      }
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'm') {
        e.preventDefault();
        setIsMinimized(true);
      }
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'r') {
        e.preventDefault();
        setIsMinimized(false);
      }
    };

    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, toggleCollapse]);

  // Memoize modal styles to prevent recalculation on every render
  const modalStyle = useMemo(
    () => ({
      left: position.x,
      top: position.y,
      width: size.width,
      height: isCollapsed ? COLLAPSED_HEIGHT : size.height,
      minWidth: 630,
      minHeight: isCollapsed ? COLLAPSED_HEIGHT : 300,
      maxWidth: '90vw',
      maxHeight: '90vh',
      transition: isDragging || isResizing ? 'none' : 'height 200ms ease-in-out',
    }),
    [position.x, position.y, size.width, size.height, isCollapsed, isDragging, isResizing],
  );

  if (!isOpen) return null;

  return (
    <>
      {!isMinimized && (
        <div
          data-devmodal-root
          className="fixed z-50 flex flex-col overflow-hidden rounded-8 border-2 border-blue-6 bg-grey-1 py-2 shadow-2xl"
          style={modalStyle}
        >
          {/* Header (drag handle) */}
          <div
            className={classNames(
              'flex shrink-0 cursor-move select-none items-center justify-between p-3',
              isCollapsed ? 'bg-grey-2' : '',
            )}
            onMouseDown={onHeaderMouseDown}
          >
            <div className="flex items-center gap-2">
              <div className="bg-grey-4 p-1">
                <Code className="size-4 text-grey-12" />
              </div>
              <div className="flex flex-col">
                <h2 className="font-semibold text-20 text-grey-12">Developer Tools</h2>
                {!isCollapsed && (
                  <p className="text-12 text-grey-10">
                    <i>&ldquo;It worked on my machine&rdquo;</i>
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setIsMinimized(true)}
                className="rounded p-1 text-grey-9 hover:bg-grey-2 hover:text-grey-11"
                title="Minimize (Ctrl+Shift+M)"
                type="button"
              >
                <Remove className="size-4" />
              </button>
              <button
                onClick={toggleCollapse}
                className="rounded p-1 text-grey-9 hover:bg-grey-2 hover:text-grey-11"
                title="Collapse (Ctrl+Shift+C)"
                type="button"
              >
                {isCollapsed ? <DoubleChevronDown className="size-4" /> : <DoubleChevronUp className="size-4" />}
              </button>
              <button
                onClick={onClose}
                className="rounded p-1 text-grey-9 hover:bg-grey-2 hover:text-grey-11"
                type="button"
              >
                <Close className="size-4" />
              </button>
            </div>
          </div>

          {/* Body */}
          {!isCollapsed && (
            <div className="flex min-h-0 flex-1 flex-col">
              {/* Custom Tab Navigation */}
              <div className="flex shrink-0 border-grey-4 border-b px-6">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={classNames(
                      '-mb-px cursor-pointer select-none px-2 py-1 font-semibold text-14 text-grey-11 hover:text-grey-12',
                      activeTab === tab.id
                        ? 'border-b-2 border-b-blue-9 text-grey-12'
                        : 'border-b-2 border-b-transparent',
                    )}
                    type="button"
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Tab Content */}
              <div className="min-h-0 flex-1 overflow-hidden pt-4">
                {activeTab === 'overview' && <OverviewTab onCopy={copy} />}

                {activeTab === 'flags' && (
                  <FeatureFlagsTab
                    flagOverrides={flagOverrides}
                    expandedFlags={expandedFlags}
                    jsonInputs={jsonInputs}
                    onToggleFlag={handleToggleFlag}
                    onResetFlags={handleResetFlags}
                    setExpandedFlags={setExpandedFlags}
                    setJsonInputs={setJsonInputs}
                  />
                )}

                {activeTab === 'api' && (
                  <APIRequestsTab
                    apiRequests={apiRequests}
                    leftPanelWidth={leftPanelWidth}
                    setLeftPanelWidth={setLeftPanelWidth}
                    setApiRequests={setApiRequests}
                    onCopy={copy}
                  />
                )}

                {activeTab === 'tools' && (
                  <ToolsTab
                    flagOverrides={flagOverrides}
                    apiRequests={apiRequests}
                    isInspecting={isInspecting}
                    setIsInspecting={setIsInspecting}
                  />
                )}

                {activeTab === 'modals' && <ModalsTab />}

                {activeTab === 'analytics' && (
                  <AnalyticsTab
                    analyticsEvents={analyticsEvents}
                    analyticsLeftPanelWidth={analyticsLeftPanelWidth}
                    setAnalyticsLeftPanelWidth={setAnalyticsLeftPanelWidth}
                    setAnalyticsEvents={setAnalyticsEvents}
                    onCopy={copy}
                  />
                )}
              </div>
            </div>
          )}

          {/* Resize handle */}
          {!isCollapsed && (
            <div
              className="absolute right-0 bottom-0 size-4 cursor-nw-resize"
              onMouseDown={onResizeMouseDown}
              style={{
                background: 'linear-gradient(-45deg, transparent 30%, #cbd5e1 30%, #cbd5e1 70%, transparent 70%)',
              }}
            />
          )}
        </div>
      )}

      {/* Dock icon when minimized */}
      {isMinimized && (
        <div
          className="fixed bottom-0 left-1/2 z-40 -translate-x-1/2 cursor-pointer"
          onClick={() => setIsMinimized(false)}
          title="Click to restore Developer Tools"
        >
          <div className="flex items-center gap-2 rounded-t-8 bg-blue-9 px-4 py-2 font-semibold shadow-lg transition-all duration-200 hover:scale-105">
            <Code className="size-4 text-grey-1" />
            <span className="text-14 text-grey-1">Dev Tools</span>
          </div>
        </div>
      )}

      {/* Element Inspector Overlay */}
      {isInspecting && elementInfo && <ElementInspectorOverlay elementInfo={elementInfo} />}
    </>
  );
});
